import React, { createContext, useEffect, useState } from 'react';
import moment from 'moment';

// eslint-disable-next-line import/no-cycle
import { useGlobalContext } from '../hooks/context';
import {
  ExpectedDPMOChangeEvent,
  FeedbackParam,
  Productivity,
  ProductivityReportItem,
  QualityEvent,
  QualityReportItem,
  SQPRQualityError,
  UnknownIdleTimeMetric,
  WeeklySpprSummary,
  WeeklySqprSummary,
  AuditItem,
} from '../types/backendService';
import { isAuditEnabledForUser, isIdleTimeReportingEnabledForUser, isReportingEnabledForUser } from '../util/allow_lists';

export interface UserDataContextValues {
  sessionStartTime: moment.Moment;
  feedbacks: FeedbackParam[] | undefined;
  spprSummaries: WeeklySpprSummary[] | undefined;
  sqprSummaries: WeeklySqprSummary[] | undefined;
  
  currentWeekProductivity: Productivity[] | undefined;
  currentWeekQualityEvents: QualityEvent[] | undefined;
  selectedWeekQualityEvents: QualityEvent[] | undefined;
  selectedWeekQualityErrors: SQPRQualityError[] | undefined;
  selectedWeekProductity: Productivity[] | undefined;
  productivityReportItems: ProductivityReportItem[] | undefined;
  unknownIdleTimeMetrics: UnknownIdleTimeMetric[] | undefined;
  qualityReportItems: QualityReportItem[] | undefined;
  expectedDPMOChangeEvents: ExpectedDPMOChangeEvent[] | undefined;
  auditItems: AuditItem[] | undefined;
  spprWeeks: moment.Moment[] | undefined;
  sqprWeeks: moment.Moment[] | undefined;
  selectedSpprWeek: moment.Moment | undefined;
  selectedSqprWeek: moment.Moment | undefined;
  changeSpprWeek: (_selectedSpprWeek: moment.Moment) => void;
  changeSqprWeek: (_selectedSqprWeek: moment.Moment) => void;
  emailReport: (_reportType: string) => Promise<string>;
  showGuardrailRate: boolean;
  dataFailure: boolean;
  dataFailureError: Error | undefined;
  /**
   * We only want to send metric data for non-admins and after
   * all of their data has loaded.
   */
  areMetricsSendable: boolean;
}

const initialState: UserDataContextValues = {
  sessionStartTime: moment(),
  feedbacks: undefined,
  spprSummaries: undefined,
  sqprSummaries: undefined,
  currentWeekProductivity: undefined,
  currentWeekQualityEvents: undefined,
  selectedWeekQualityEvents: undefined,
  selectedWeekQualityErrors: undefined,
  selectedWeekProductity: undefined,
  productivityReportItems: undefined,
  unknownIdleTimeMetrics: undefined,
  qualityReportItems: undefined,
  expectedDPMOChangeEvents: undefined,
  auditItems: undefined,
  spprWeeks: undefined,
  sqprWeeks: undefined,
  selectedSpprWeek: undefined,
  selectedSqprWeek: undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeSpprWeek: (_selectedSpprWeek: moment.Moment) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changeSqprWeek: (_selectedSqprWeek: moment.Moment) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  emailReport: (_reportType: string) => new Promise((resolve) => resolve('')),
  showGuardrailRate: false,
  dataFailure: false,
  dataFailureError: undefined,
  areMetricsSendable: false,
};

// eslint-disable-next-line arrow-body-style
const workItemsContainGuardrailRate = (workItems: Productivity[]) => {
  return workItems.some((workItem) => workItem.configuredRate != null);
};

export const UserDataContext = createContext<UserDataContextValues>(initialState);

export const UserDataContextProvider = ({ children }: any) => {
  const {
    backendServiceClient,
    isLoggedIn,
    userDetails,
    userToken,
    targetUser,
    isAdminViewEnabled,
  } = useGlobalContext();

  const [feedbacks, setFeedbacks] = useState(undefined as FeedbackParam[] | undefined);
  const [spprSummaries, setSpprSummaries] = useState([] as WeeklySpprSummary[] | undefined);
  const [sqprSummaries, setSqprSummaries] = useState([] as WeeklySqprSummary[] | undefined);
  const [currentWeekProductivity, setCurrentWeekProductivity] = useState(
    [] as Productivity[] | undefined,
  );
  const [currentWeekQualityEvents, setCurrentWeekQualityEvents] = useState(
    [] as QualityEvent[] | undefined,
  );
  const [selectedWeekQualityEvents, setSelectedWeekQualityEvents] = useState(
    [] as QualityEvent[] | undefined,
  );
  const [selectedWeekQualityErrors, setSelectedWeekQualityErrors] = useState(
    [] as SQPRQualityError[] | undefined,
  );
  const [selectedWeekProductity, setSelectedWeekProductity] = useState(
    [] as Productivity[] | undefined,
  );
  const [productivityReportItems, setProductivityReportItems] = useState(
    [] as ProductivityReportItem[] | undefined,
  );
  const [unknownIdleTimeMetrics, setUnknownIdleTimeMetrics] = useState(
    [] as UnknownIdleTimeMetric[] | undefined,
  );
  const [qualityReportItems, setQualityReportItems] = useState(
    [] as QualityReportItem[] | undefined,
  );
  const [expectedDPMOChangeEvents, setExpectedDPMOChangeEvents] = useState(
    [] as ExpectedDPMOChangeEvent[] | undefined,
  );
  const [auditItems, setAuditItems] = useState(undefined as AuditItem[] | undefined);
  const [spprWeeks, setSpprWeeks] = useState([] as moment.Moment[] | undefined);
  const [sqprWeeks, setSqprWeeks] = useState([] as moment.Moment[] | undefined);
  const [selectedSpprWeek, setSelectedSpprWeek] = useState(undefined as moment.Moment | undefined);
  const [selectedSqprWeek, setSelectedSqprWeek] = useState(undefined as moment.Moment | undefined);
  const [showGuardrailRate, setShowGuardrailRate] = useState(false);
  const [dataFailure, setDataFailure] = useState(false);
  const [dataFailureError, setDataFailureError] = useState(undefined as Error | undefined);
  const [areMetricsSendable, setMetricsSendable] = useState(false);

  // Update user data whenever the target user changes
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    updateData();
  }, [targetUser]);

  // Stop sending session metrics when the user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      setMetricsSendable(false);
    }
  }, [isLoggedIn]);

  const updateData = () => {
    setFeedbacks(undefined);
    setSpprSummaries(undefined);
    setSqprSummaries(undefined);
    setCurrentWeekProductivity(undefined);
    setCurrentWeekQualityEvents(undefined);
    setSelectedWeekQualityEvents(undefined);
    setSelectedWeekQualityErrors(undefined);
    setSelectedWeekProductity(undefined);
    setProductivityReportItems(undefined);
    setUnknownIdleTimeMetrics(undefined);
    setQualityReportItems(undefined);
    setExpectedDPMOChangeEvents(undefined);
    setAuditItems(undefined);
    setSpprWeeks(undefined);
    setSqprWeeks(undefined);
    setShowGuardrailRate(false);

    const currentMoment = moment();

    if (userToken !== '') {
      const updateFeedbacks = async () => {
        const data = await backendServiceClient.getFilteredFeedbackHistory(
          userToken,
          targetUser.employeeId,
          targetUser.login,
        );
        setFeedbacks(data.feedbackParams);
      };

      const updateSppr = async () => {
        const spprOutput = await backendServiceClient.getWeeklySpprSummaries(
          userToken,
          targetUser.employeeId,
          targetUser.login,
        );
        setSpprSummaries(spprOutput.summaries);

        // eslint-disable-next-line prefer-const
        let spprWeeksWithData: moment.Moment[] = [];
        for (const item of spprOutput.summaries!) {
          if (item.rankPercentile !== undefined) {
            spprWeeksWithData.unshift(moment(item.endTime! * 1000));
          }
        }
        setSpprWeeks(spprWeeksWithData);

        if (spprWeeksWithData.length > 0) {
          spprWeeksWithData.unshift(moment().add(1, 'weeks').subtract(3, 'days').startOf('week')
            .add(3, 'days'));
          const dailyProductivityDataForWeek = await backendServiceClient.getDailyProductivityDataForWeek(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            spprWeeksWithData[0],
          );
          setSelectedWeekProductity(dailyProductivityDataForWeek.productivities);
          setShowGuardrailRate(
            workItemsContainGuardrailRate(dailyProductivityDataForWeek.productivities || []),
          );

          setSelectedSpprWeek(spprWeeksWithData[0]);
        }
      };

      const updateSqpr = async () => {
        const sqprOutput = await backendServiceClient.getWeeklySqprSummaries(
          userToken,
          targetUser.employeeId,
          targetUser.login,
        );
        setSqprSummaries(sqprOutput.summaries);

        // eslint-disable-next-line prefer-const
        let sqprWeeksWithData: moment.Moment[] = [];
        for (const item of sqprOutput.summaries!) {
          if (item.percentile !== undefined) {
            sqprWeeksWithData.unshift(moment(item.endTime! * 1000));
          }
        }
        setSqprWeeks(sqprWeeksWithData);

        if (sqprWeeksWithData.length > 0) {
          const qualityErrorsResponse = await backendServiceClient.getSQPRQualityErrors(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            sqprWeeksWithData[0],
          );
          setSelectedWeekQualityErrors(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            createQualityErrorRows(qualityErrorsResponse.sqprQualityErrors),
          );

          /**
           * Note: An SQPR period is for data with a 2 day delay
           * https://sage.amazon.com/posts/236784
           */
          const qualityEventsResponse = await backendServiceClient.getQualityEvents(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            sqprWeeksWithData[0].clone().subtract(2, 'days'),
          );

          setSelectedWeekQualityEvents(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            filterQualityEventsForSQPR(
              qualityEventsResponse.qualityEvents,
              qualityErrorsResponse.sqprQualityErrors,
            ),
          );
          setSelectedSqprWeek(sqprWeeksWithData[0]);
        }
      };

      const updateHourlyData = async () => {
        const hourlyData = await backendServiceClient.getHourlyProductivityData(
          userToken,
          targetUser.employeeId,
          targetUser.login,
        );
        setCurrentWeekProductivity(hourlyData.productivities);
      };

      const updateQualityEvents = async () => {
        const qualityEventsOutput = await backendServiceClient.getQualityEvents(
          userToken,
          targetUser.employeeId,
          targetUser.login,
          currentMoment,
        );
        setCurrentWeekQualityEvents(qualityEventsOutput.qualityEvents);
      };

      const updateProductivityReportItems = async () => {
        if (isReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
          const productivityReportOutput = await backendServiceClient.getProductivityReport(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            userDetails.warehouseId,
          );
          // Show more recent periods first in the list
          setProductivityReportItems(
            productivityReportOutput.productivityReportItems?.sort(
              (itemA, itemB) => itemB.periodStart! - itemA.periodStart!,
            ),
          );
        }
      };

      const updateUnknownIdleTimeMetrics = async () => {
        if (isIdleTimeReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
          const unknownIdleTimeMetricsOutput = await backendServiceClient.getUnknownIdleTimeMetrics(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            userDetails.warehouseId,
          );
          setUnknownIdleTimeMetrics(unknownIdleTimeMetricsOutput.unknownIdleTimeMetrics);
        }
      };

      const updateQualityReportItems = async () => {
        if (isReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
          const qualityReportOutput = await backendServiceClient.getQualityReport(
            userToken,
            targetUser.employeeId,
            targetUser.login,
            userDetails.warehouseId,
          );
          // Show more recent periods first in the list
          setQualityReportItems(
            qualityReportOutput.qualityReportItems?.sort(
              (itemA, itemB) => itemB.periodStart! - itemA.periodStart!,
            ),
          );
        }
      };

      const updateExpectedDPMOChangeEvents = async () => {
        if (isReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
          const events = await backendServiceClient.getExpectedDPMOChangeEvents(
            userToken,
            targetUser.employeeId,
            targetUser.login,
          );
          // two level sort: first to show more recent changes at top, then by error family
          setExpectedDPMOChangeEvents(
            events.expectedDPMOChangeEvents
              ?.sort((itemA, itemB) => itemB.startTime! - itemA.startTime!)
              ?.sort((itemA, itemB) => itemB.errorFamily!.localeCompare(itemA.errorFamily!)),
          );
        }
      };

      const updateAuditItems = async () => {
        if (isAuditEnabledForUser(userDetails, isAdminViewEnabled)) {
          const auditReport = await backendServiceClient.getAuditReport(
            userToken,
            targetUser.login,
            targetUser.employeeId,
          );
          setAuditItems(auditReport.auditItems);
        }
      };

      // Run in parallel
      (async () => {
        try {
          await Promise.all([
            updateFeedbacks(),
            updateSppr(),
            updateSqpr(),
            updateHourlyData(),
            updateQualityEvents(),
            updateProductivityReportItems(),
            updateUnknownIdleTimeMetrics(),
            updateQualityReportItems(),
            updateExpectedDPMOChangeEvents(),
            updateAuditItems(),
          ]);
          setMetricsSendable(!userDetails.isAdmin);
        } catch (error) {
          setDataFailureError(error as Error);
          setDataFailure(true);
        }
      })();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const changeSpprWeek = async (selectedSpprWeek: moment.Moment) => {
    setSelectedWeekProductity(undefined);
    try {
      const dailyProductivityDataForWeek = await backendServiceClient.getDailyProductivityDataForWeek(
        userToken,
        targetUser.employeeId,
        targetUser.login,
        selectedSpprWeek,
      );

      setSelectedWeekProductity(dailyProductivityDataForWeek.productivities);
      /**
       * If guardRailRate was present for a previous week then we still want to show Guardrail Rate
       * in the metric dropdown so that the user
       * doesn't need to re-select Guardrail Rate after moving to a week that has Guardrail Rate again.
       */
      setShowGuardrailRate(
        showGuardrailRate ||
          workItemsContainGuardrailRate(dailyProductivityDataForWeek.productivities || []),
      );
      setSelectedSpprWeek(selectedSpprWeek);
    } catch (error) {
      setDataFailureError(error as Error);
      setDataFailure(true);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const changeSqprWeek = async (selectedSqprWeek: moment.Moment) => {
    setSelectedWeekQualityErrors(undefined);
    setSelectedWeekQualityEvents(undefined);

    try {
      const qualityErrorsResponse = await backendServiceClient.getSQPRQualityErrors(
        userToken,
        targetUser.employeeId,
        targetUser.login,
        selectedSqprWeek,
      );
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setSelectedWeekQualityErrors(createQualityErrorRows(qualityErrorsResponse.sqprQualityErrors));

      const qualityEventsResponse = await backendServiceClient.getQualityEvents(
        userToken,
        targetUser.employeeId,
        targetUser.login,
        selectedSqprWeek.clone().subtract(2, 'days'),
      );
      setSelectedWeekQualityEvents(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        filterQualityEventsForSQPR(
          qualityEventsResponse.qualityEvents,
          qualityErrorsResponse.sqprQualityErrors,
        ),
      );
      setSelectedSqprWeek(selectedSqprWeek);
    } catch (error) {
      setDataFailureError(error as Error);
      setDataFailure(true);
    }
  };

  /**
   * Filter a list of quality events to remove any quality events that were not counted towards SQPR.
   *
   * @param qualityEvents
   * @param sqprQualityErrors
   * @returns qualityEvents within the SQPR period
   */
  const filterQualityEventsForSQPR = (
    qualityEvents: QualityEvent[] | undefined,
    sqprQualityErrors: SQPRQualityError[] | undefined,
  ) => {
    const applicableQualityEventIds = new Set<string>();
    for (const qualityError of sqprQualityErrors || []) {
      if (
        !qualityError.isErrorTypeExcluded &&
        qualityError.unitsProcessed! > 0 &&
        qualityError.unitsProcessed! >= qualityError.minimumUnits!
      ) {
        (qualityError.qualityEventIds || []).forEach((id) => applicableQualityEventIds.add(id));
      }
    }
    return (qualityEvents || []).filter(
      (event: QualityEvent) => event.isUsedInSQPR && applicableQualityEventIds.has(event.id!),
    );
  };

  /**
   * Create the quality error errors to display in the table in the "Detailed Weekly Quality Review Metrics By Week"
   * table in the "Weekly Quality Review Summary" tab of the Quality page.
   * We exlude all error types where isErrorTypeExcluded is true, units processed are 0, or units processed is
   * less than the minimum units. We also add a "total" row.
   * Logic is similar to the ADAPT website:
   * https://code.amazon.com/packages/FemidaWebsiteStatic/blobs/99f0500cd3205c70061711f343b7d977117281dd/--/webapp/static-content/performance-metrics/sqpr-calculation/sqpr-calculation-controller.js#L72
   *
   * @param sqprQualityErrors
   * @returns quality error rows
   */
  const createQualityErrorRows = (
    sqprQualityErrors: SQPRQualityError[] | undefined,
  ): SQPRQualityError[] => {
    if (sqprQualityErrors === undefined || sqprQualityErrors.length === 0) {
      return [];
    }

    // eslint-disable-next-line prefer-destructuring
    const warehouseId = sqprQualityErrors[0].warehouseId;
    // eslint-disable-next-line prefer-destructuring
    const metricTime = sqprQualityErrors[0].metricTime;
    // eslint-disable-next-line prefer-const
    let qualityErrors: SQPRQualityError[] = [];

    let expectedDpmoPartials = 0;
    let unitsProcessedTotal = 0;
    let qualityEventsTotal = 0;
    const errorFamiliesUsed = new Set<string>();

    for (const qualityError of sqprQualityErrors) {
      if (
        qualityError.unitsProcessed! <= 0 ||
        qualityError.unitsProcessed! < qualityError.minimumUnits!
      ) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (!errorFamiliesUsed.has(qualityError.lcFamilyId!)) {
        errorFamiliesUsed.add(qualityError.lcFamilyId!);
        unitsProcessedTotal += qualityError.unitsProcessed!;
        expectedDpmoPartials += qualityError.unitsProcessed! * qualityError.expectedDpmo!;
      }

      if (qualityError.isErrorTypeExcluded) {
        qualityError.qualityEvents = 0;
      }

      qualityEventsTotal += qualityError.qualityEvents!;
      qualityErrors.push(qualityError);
    }

    qualityErrors.push({
      lcFamilyId: 'Total',
      errorType: '-',
      unitsProcessed: unitsProcessedTotal,
      minimumUnits: 0,
      qualityEvents: qualityEventsTotal,
      expectedDpmo: Math.round(expectedDpmoPartials / unitsProcessedTotal),
      isErrorTypeExcluded: false,
      warehouseId,
      metricTime,
      qualityEventIds: [],
    });

    return qualityErrors;
  };

  const emailReport = async (reportType: string) => {
    const response = await backendServiceClient.sendEmailReport(
      userToken,
      targetUser.employeeId,
      targetUser.login,
      reportType,
      userDetails.warehouseId,
    );

    return response.emailAddress!;
  };

  return (
    <UserDataContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        sessionStartTime: initialState.sessionStartTime,
        feedbacks,
        spprSummaries,
        sqprSummaries,
        currentWeekProductivity,
        currentWeekQualityEvents,
        selectedWeekQualityEvents,
        selectedWeekQualityErrors,
        selectedWeekProductity,
        productivityReportItems,
        unknownIdleTimeMetrics,
        qualityReportItems,
        expectedDPMOChangeEvents,
        auditItems,
        spprWeeks,
        sqprWeeks,
        selectedSpprWeek,
        selectedSqprWeek,
        changeSpprWeek,
        changeSqprWeek,
        emailReport,
        showGuardrailRate,
        dataFailure,
        dataFailureError,
        areMetricsSendable,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};
